import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Input,
  Label,
  ModalBody,
  Row,
} from "reactstrap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DIVISION_QUERY_KEY,
  VIEW_DIVISION_QUERY_KEY,
  divisionService,
} from "../../../../../Services/divisionService";
import { useState } from "react";
import { toast } from "react-toastify";
import { Btn } from "../../../../../AbstractElements";
import { useAppSelector } from "../../../../../ReduxToolkit/Hooks";

interface CreateDivisionFormProps {
  toggle?: () => void; // optional
}

const INITIAL_STATE = {
  name: "",
  status: -1,
  created_by: undefined,
  updated_by: undefined,
};

interface CreateDivisionFormProps {
  toggle?: () => void;
  company_code: string; // required
}

const CreateDivisionForm: React.FC<CreateDivisionFormProps> = ({
  company_code,
  toggle,
}) => {
  const queryClient = useQueryClient();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const user = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState(INITIAL_STATE);

  const createDivisionMutation = useMutation({
    mutationFn: divisionService.createDivision,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VIEW_DIVISION_QUERY_KEY });

      toast.success("Division has been created successfully.");

      setFormData(INITIAL_STATE);

      if (toggle) {
        toggle();
      }
    },
    onError: (error: any) => {
      const responseData = error.response?.data;

      if (responseData?.errors) {
        const firstErrorKey = Object.keys(responseData.errors)[0];
        const errorMessage = responseData.errors[firstErrorKey][0];
        toast.error(errorMessage);
      } else if (responseData?.message) {
        toast.error(responseData.message);
      } else {
        toast.error(
          "Unable to create division. Please verify the entered information.",
        );
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSelectedStatus = (status: number) => {
    setFormData((prev) => ({ ...prev, status: status }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.status === -1) {
      toast.warn("Please select a Status");
      return;
    }

    if (!user) {
      toast.error("User not authenticated");
      return;
    }

    const payload = {
      ...formData,
      created_by: user.id,
      updated_by: user.id,
      company_code: company_code,
    };
    createDivisionMutation.mutate(payload);
  };

  return (
    <ModalBody>
      <Form className="needs-validation" onSubmit={handleSubmit}>
        <Row className="g-3">
          <Col md="12">
            <Label>
              Division Name<b className="text-danger">*</b>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter New Division"
              maxLength={150}
              minLength={3}
              required
              value={formData.name}
              onChange={handleInputChange}
            />
          </Col>

          <Col md="12">
            <Label>
              Status<b className="text-danger">*</b>
            </Label>
            <Input
              className="form-control mb-2"
              value={formData.status}
              id="status"
              name="status"
              type="select"
              onChange={(e) => handleSelectedStatus(Number(e.target.value))}
            >
              <option disabled value={-1}>
                Select Status
              </option>
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </Input>
          </Col>

          <Col xs="12">
            <Btn
              color="primary"
              className="w-100"
              type="submit"
              disabled={createDivisionMutation.isPending}
            >
              {createDivisionMutation.isPending
                ? "Creating Division..."
                : "Create"}
            </Btn>
          </Col>
        </Row>
      </Form>
    </ModalBody>
  );
};

export default CreateDivisionForm;
